/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path utils
 * @description Notification about MyTasks
 */
import { SERVER_ADDR_COOKIE_NAME } from "./../apicaller/api-consts";

/**
 * CRA may emit `/tdf/static/media/...` when the app is hosted under a basename (e.g. `/tdf/`).
 * Those files are always served from `/static/media/...` at the site root. Tenant-prefixed
 * paths also appear inside theme JSON after `setServerUrls` injects `REACT_APP_SERVER_URL`
 * after every `url(` (e.g. `url(/tdf/static/media/background.HASH.png)`), which otherwise
 * resolves to HTML and breaks the landing background.
 */
export const normalizeBundledAssetUrl = (url: string): string => {
  if (!url || typeof url !== "string") return url;
  let s = url;
  let prev = "";
  while (s !== prev) {
    prev = s;
    s = s.replace(/\/(?!static\/)[^/\s"')]+\/static\/(media|js)\//g, "/static/$1/");
  }
  return s;
};

const getFileApiURL = (filepath: string) => {
  // Use the same serverUrl resolution logic as api-caller.ts
  const cookieServerAddr = readCookieValue(SERVER_ADDR_COOKIE_NAME);
  const serverUrl = cookieServerAddr || process.env.REACT_APP_SERVER_URL || "";
  
  // Normalize serverUrl - handle "." case and ensure proper format
  if (serverUrl === "." || serverUrl === "") {
    // Use relative URL if serverUrl is "." or empty (same origin)
    // Always return absolute path starting with /
    if (filepath.includes("/api/files/?file=")) {
      const result = filepath.startsWith("/") ? filepath : `/${filepath}`;
      return result;
    }
    // Ensure filepath starts with / for proper URL construction
    const normalizedFilepath = filepath.startsWith("/") ? filepath : `/${filepath}`;
    return `/api/files/?file=${normalizedFilepath}`;
  } else {
    // If serverUrl doesn't start with http:// or https://, treat it as relative
    if (!serverUrl.startsWith("http://") && !serverUrl.startsWith("https://")) {
      // Treat as relative path, ensure it starts with /
      const normalizedServerUrl = serverUrl.startsWith("/") ? serverUrl : `/${serverUrl}`;
      let baseURL = normalizedServerUrl.endsWith("/") ? normalizedServerUrl.slice(0, -1) : normalizedServerUrl;
      baseURL = baseURL.endsWith("/api") ? baseURL : `${baseURL}/api`;
      
      if (filepath.includes("/api/files/?file=")) {
        const cleanPath = filepath.replace(/^\/api\//, "");
        return `${baseURL}/${cleanPath}`;
      }
      const normalizedFilepath = filepath.startsWith("/") ? filepath : `/${filepath}`;
      return `${baseURL}/files/?file=${normalizedFilepath}`;
    } else {
      // Full URL with protocol
      let baseURL = serverUrl.endsWith("/") ? serverUrl.slice(0, -1) : serverUrl;
      baseURL = baseURL.endsWith("/api") ? baseURL : `${baseURL}/api`;
      
      if (filepath.includes("/api/files/?file=")) {
        const cleanPath = filepath.replace(/^\/api\//, "");
        return `${baseURL}/${cleanPath}`;
      }
      const normalizedFilepath = filepath.startsWith("/") ? filepath : `/${filepath}`;
      return `${baseURL}/files/?file=${normalizedFilepath}`;
    }
  }
};
const checkIfExternal = (link: string): boolean => {
  const host = window.location.hostname;
  const linkHost = (): string => {
    if (/^https?:\/\//.test(link)) {
      const parser = document.createElement("a");
      parser.href = link;
      return parser.hostname;
    }
    return window.location.hostname;
  };
  return host !== linkHost();
};
const isValidUrl = (string: string) => {
  try {
    // eslint-disable-next-line no-new
    new URL(string);
  } catch (_) {
    return false;
  }
  return true;
};
// url-reformer
// make sure that url path ends with a slash
const makeUrlPathEndingWithSlash = (aUrl: string): string | undefined => {
  let newUrl;
  // if have query part and path does not end to slash, make ? be /?
  if (aUrl.indexOf("?") !== -1) {
    if (aUrl.indexOf("/?") === -1) {
      newUrl = aUrl.replace("?", "/?");
    }
  }
  // no query part, then add slash if not ending with it already
  else if (!aUrl.endsWith("/")) {
    newUrl = `${aUrl}/`;
  }
  // undefined indicates no change is needed
  return newUrl;
};
function readCookieValue(name: string) {
  // from: https://stackoverflow.com/questions/5639346/what-is-the-shortest-function-for-reading-a-cookie-by-name-in-javascript
  return (document.cookie.match(`(^|;) *${name}=([^;]*)`) || "")[2];
}
const isValidId = (a: string): boolean => {
  return a.match(/^(PG|PO|SC)(\.\d+)+$/) !== null;
};
// check if element id is formed PG.1 or not
const isModelId = (a: string): boolean => {
  return a.match(/^(PG|PO|SC)\.\d+$/) !== null;
};
// check if element id is formed PG.1.2 or not
const isElementId = (a: string): boolean => {
  return a.match(/^(PG|PO|SC)\.\d+\.\d+$/) !== null;
};
// check if element id is formed PG.1.2.3 or not
const isInstanceId = (a: string): boolean => {
  return a.match(/^(PG|PO|SC)+\.\d+\.\d+\.\d+$/) !== null;
};
const isMongoId = (id: string): boolean => {
  return /^[a-f0-9]{24}$/i.test(id);
};
export {
  checkIfExternal,
  makeUrlPathEndingWithSlash,
  readCookieValue,
  isElementId,
  isInstanceId,
  isModelId,
  isValidId,
  isValidUrl,
  isMongoId,
  getFileApiURL,
};
